import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulesAPI, routesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Plus,
  Edit,
  Trash2,
  Clock,
  MapPin,
  DollarSign,
  Users,
  Calendar,
  Filter,
  X,
  Save,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const scheduleSchema = yup.object({
  route_id: yup.string().required('Route is required'),
  departure_time: yup.string().required('Departure time is required'),
  arrival_time: yup.string().required('Arrival time is required'),
  days_of_week: yup.array().min(1, 'At least one day must be selected').required('Days are required'),
  adult_price: yup.number().min(0, 'Must be zero or positive').required('Adult price is required'),
  child_price: yup.number().min(0, 'Must be zero or positive').required('Child price is required'),
  senior_price: yup.number().min(0, 'Must be zero or positive').required('Senior price is required'),
  max_capacity: yup.number().integer().positive('Must be positive').required('Capacity is required'),
  valid_from: yup.date(),
  valid_until: yup.date()
});

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Schedules = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [filters, setFilters] = useState({
    route_id: '',
    active_only: 'true'
  });

  const queryClient = useQueryClient();

  // Fetch schedules
  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ['schedules', filters],
    queryFn: () => schedulesAPI.getAll(filters)
  });

  // Fetch routes for dropdown
  const { data: routesData } = useQuery({
    queryKey: ['routes'],
    queryFn: () => routesAPI.getAll()
  });

  const schedules = schedulesData?.data?.data?.schedules || [];
  const routes = routesData?.data?.data?.routes || [];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      days_of_week: [],
      max_capacity: 50,
      valid_from: format(new Date(), 'yyyy-MM-dd'),
      valid_until: format(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
    }
  });

  const watchedDays = watch('days_of_week') || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => schedulesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['schedules']);
      toast.success('Schedule created successfully');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create schedule');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schedulesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['schedules']);
      toast.success('Schedule updated successfully');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update schedule');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => schedulesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['schedules']);
      toast.success('Schedule deactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to deactivate schedule');
    }
  });

  const openModal = (schedule = null) => {
    setEditingSchedule(schedule);
    if (schedule) {
      // Populate form with existing data
      Object.keys(schedule).forEach(key => {
        if (key === 'days_of_week') {
          setValue(key, schedule[key] || []);
        } else if (key === 'valid_from' || key === 'valid_until') {
          setValue(key, schedule[key] ? format(new Date(schedule[key]), 'yyyy-MM-dd') : '');
        } else {
          setValue(key, schedule[key]);
        }
      });
    } else {
      reset();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      if (editingSchedule) {
        await updateMutation.mutateAsync({ id: editingSchedule.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error handled in mutation
    }
  };

  const handleDayChange = (dayIndex) => {
    const currentDays = watchedDays || [];
    const newDays = currentDays.includes(dayIndex)
      ? currentDays.filter(d => d !== dayIndex)
      : [...currentDays, dayIndex].sort((a, b) => a - b);
    setValue('days_of_week', newDays);
  };

  const handleDelete = (schedule) => {
    if (window.confirm(`Are you sure you want to deactivate this schedule? This will make it unavailable for new bookings.`)) {
      deleteMutation.mutate(schedule.id);
    }
  };

  const getDaysDisplay = (days) => {
    if (!days || days.length === 0) return 'None';
    if (days.length === 7) return 'Daily';
    return days.map(day => dayNames[day].slice(0, 3)).join(', ');
  };

  const getRouteDisplay = (schedule) => {
    // Use the route info directly from schedule data (already joined in API response)
    if (schedule.origin && schedule.destination) {
      return `${schedule.origin} → ${schedule.destination}`;
    }
    // Fallback to routes array if needed
    const route = routes.find(r => r.id === schedule.route_id);
    return route ? `${route.origin} → ${route.destination}` : 'Unknown Route';
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedules</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage bus schedules, routes, and pricing
          </p>
        </div>
        {isAdmin && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => openModal()}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Schedule
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="route_filter" className="form-label">
              Filter by Route
            </label>
            <select
              id="route_filter"
              className="form-input"
              value={filters.route_id}
              onChange={(e) => setFilters({ ...filters, route_id: e.target.value })}
            >
              <option value="">All Routes</option>
              {routes.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.origin} → {route.destination}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="active_filter" className="form-label">
              Status
            </label>
            <select
              id="active_filter"
              className="form-input"
              value={filters.active_only}
              onChange={(e) => setFilters({ ...filters, active_only: e.target.value })}
            >
              <option value="true">Active Only</option>
              <option value="false">All Schedules</option>
            </select>
          </div>
        </div>
      </div>

      {/* Schedules List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading schedules...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No schedules found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isAdmin ? 'Get started by adding a new schedule.' : 'No schedules match your filters.'}
            </p>
            {isAdmin && (
              <div className="mt-6">
                <button onClick={() => openModal()} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Schedule
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Route</th>
                  <th className="table-header">Times</th>
                  <th className="table-header">Days</th>
                  <th className="table-header">Pricing</th>
                  <th className="table-header">Capacity</th>
                  <th className="table-header">Valid Period</th>
                  <th className="table-header">Status</th>
                  {isAdmin && <th className="table-header">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm font-medium text-gray-900">
                          {getRouteDisplay(schedule)}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div>{schedule.departure_time}</div>
                          {schedule.arrival_time && (
                            <div className="text-xs text-gray-500">→ {schedule.arrival_time}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {getDaysDisplay(schedule.days_of_week)}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <DollarSign className="h-3 w-3 text-gray-400 mr-1" />
                          <span>A: ${parseFloat(schedule.adult_price).toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          C: ${parseFloat(schedule.child_price).toFixed(2)} |
                          S: ${parseFloat(schedule.senior_price).toFixed(2)}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center text-sm text-gray-900">
                        <Users className="h-4 w-4 text-gray-400 mr-1" />
                        {schedule.max_capacity}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {schedule.valid_from ? format(new Date(schedule.valid_from), 'MMM d, yyyy') : 'No start'}
                        <div className="text-xs text-gray-500">
                          to {schedule.valid_until ? format(new Date(schedule.valid_until), 'MMM d, yyyy') : 'No end'}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${schedule.is_active ? 'badge-confirmed' : 'badge-cancelled'}`}>
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openModal(schedule)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit schedule"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {schedule.is_active && (
                            <button
                              onClick={() => handleDelete(schedule)}
                              disabled={deleteMutation.isLoading}
                              className="text-red-600 hover:text-red-900"
                              title="Deactivate schedule"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Route Selection */}
              <div>
                <label htmlFor="route_id" className="form-label">
                  Route *
                </label>
                <select
                  id="route_id"
                  {...register('route_id')}
                  className={`form-input ${errors.route_id ? 'border-red-300' : ''}`}
                >
                  <option value="">Select a route</option>
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.origin} → {route.destination}
                    </option>
                  ))}
                </select>
                {errors.route_id && <p className="form-error">{errors.route_id.message}</p>}
              </div>

              {/* Times */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="departure_time" className="form-label">
                    Departure Time *
                  </label>
                  <input
                    id="departure_time"
                    type="time"
                    {...register('departure_time')}
                    className={`form-input ${errors.departure_time ? 'border-red-300' : ''}`}
                  />
                  {errors.departure_time && <p className="form-error">{errors.departure_time.message}</p>}
                </div>

                <div>
                  <label htmlFor="arrival_time" className="form-label">
                    Arrival Time *
                  </label>
                  <input
                    id="arrival_time"
                    type="time"
                    {...register('arrival_time')}
                    className={`form-input ${errors.arrival_time ? 'border-red-300' : ''}`}
                  />
                  {errors.arrival_time && <p className="form-error">{errors.arrival_time.message}</p>}
                </div>
              </div>

              {/* Days of Week */}
              <div>
                <label className="form-label">
                  Days of Week *
                </label>
                <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-7">
                  {dayNames.map((day, index) => (
                    <label key={index} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={watchedDays.includes(index)}
                        onChange={() => handleDayChange(index)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{day.slice(0, 3)}</span>
                    </label>
                  ))}
                </div>
                {errors.days_of_week && <p className="form-error">{errors.days_of_week.message}</p>}
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="adult_price" className="form-label">
                    Adult Price *
                  </label>
                  <input
                    id="adult_price"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('adult_price')}
                    className={`form-input ${errors.adult_price ? 'border-red-300' : ''}`}
                  />
                  {errors.adult_price && <p className="form-error">{errors.adult_price.message}</p>}
                </div>

                <div>
                  <label htmlFor="child_price" className="form-label">
                    Child Price *
                  </label>
                  <input
                    id="child_price"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('child_price')}
                    className={`form-input ${errors.child_price ? 'border-red-300' : ''}`}
                  />
                  {errors.child_price && <p className="form-error">{errors.child_price.message}</p>}
                </div>

                <div>
                  <label htmlFor="senior_price" className="form-label">
                    Senior Price *
                  </label>
                  <input
                    id="senior_price"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('senior_price')}
                    className={`form-input ${errors.senior_price ? 'border-red-300' : ''}`}
                  />
                  {errors.senior_price && <p className="form-error">{errors.senior_price.message}</p>}
                </div>
              </div>

              {/* Capacity */}
              <div>
                <label htmlFor="max_capacity" className="form-label">
                  Maximum Capacity *
                </label>
                <input
                  id="max_capacity"
                  type="number"
                  min="1"
                  max="100"
                  {...register('max_capacity')}
                  className={`form-input ${errors.max_capacity ? 'border-red-300' : ''}`}
                />
                {errors.max_capacity && <p className="form-error">{errors.max_capacity.message}</p>}
              </div>

              {/* Valid Period */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="valid_from" className="form-label">
                    Valid From
                  </label>
                  <input
                    id="valid_from"
                    type="date"
                    {...register('valid_from')}
                    className={`form-input ${errors.valid_from ? 'border-red-300' : ''}`}
                  />
                  {errors.valid_from && <p className="form-error">{errors.valid_from.message}</p>}
                </div>

                <div>
                  <label htmlFor="valid_until" className="form-label">
                    Valid Until
                  </label>
                  <input
                    id="valid_until"
                    type="date"
                    {...register('valid_until')}
                    className={`form-input ${errors.valid_until ? 'border-red-300' : ''}`}
                  />
                  {errors.valid_until && <p className="form-error">{errors.valid_until.message}</p>}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingSchedule ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;